import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaBuilding, FaArrowLeft, FaEdit, FaFax, FaEnvelope,
  FaMapMarkerAlt, FaSave, FaTimes, FaPhoneAlt, FaGlobe, FaUser, FaPlus,
  FaClock, FaUsers, FaDollarSign, FaTruck, FaExclamationTriangle
} from 'react-icons/fa';
import './SheriffOfficeDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const JailDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [jail, setJail] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedJail, setEditedJail] = useState(null);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [newStaff, setNewStaff] = useState({
    name: '',
    title: '',
    phone: '',
    phone_ext: '',
    email: '',
    notes: ''
  });
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchJailDetails();
  }, [id]);

  const fetchJailDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/jails/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setJail(data.data);
        setEditedJail(data.data);
        setStaff(data.data.staff || []);
        setError('');
      } else {
        setError('Jail not found');
      }
    } catch (err) {
      console.error('Error fetching jail details:', err);
      setError('Failed to load jail details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveJailEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/jails/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editedJail)
      });

      const data = await response.json();

      if (data.success) {
        setJail(data.data);
        setEditedJail(data.data);
        setEditMode(false);
      } else {
        alert('Failed to update jail');
      }
    } catch (err) {
      console.error('Error updating jail:', err);
      alert('Failed to update jail');
    }
  };

  const handleCancelEdit = () => {
    setEditedJail(jail);
    setEditMode(false);
  };

  const handleInputChange = (field, value) => {
    setEditedJail(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddStaff = async () => {
    if (!newStaff.name || !newStaff.title) {
      alert('Name and title are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/jails/${id}/staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newStaff)
      });

      const data = await response.json();

      if (data.success) {
        await fetchJailDetails();
        setNewStaff({
          name: '',
          title: '',
          phone: '',
          phone_ext: '',
          email: '',
          notes: ''
        });
        setShowAddStaffModal(false);
      } else {
        alert('Failed to add staff member');
      }
    } catch (err) {
      console.error('Error adding staff:', err);
      alert('Failed to add staff member');
    }
  };

  const handleStaffInputChange = (field, value) => {
    setNewStaff(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="office-detail-container">
        <div className="loading">Loading jail details...</div>
      </div>
    );
  }

  if (error || !jail) {
    return (
      <div className="office-detail-container">
        <div className="error-message">{error || 'Jail not found'}</div>
        <button className="btn-back" onClick={() => navigate('/address-book/jails')}>
          <FaArrowLeft /> Back to Jails
        </button>
      </div>
    );
  }

  const displayData = editMode ? editedJail : jail;

  // Build full address string
  const fullAddress = [
    displayData.address_street,
    displayData.city,
    displayData.address_state,
    displayData.address_zip
  ].filter(Boolean).join(', ');

  // Build staff display with breakdown
  const staffDisplay = displayData.total_staff
    ? `${displayData.total_staff} total${displayData.sworn_staff || displayData.civilian_staff
        ? ` (${[
            displayData.sworn_staff ? `${displayData.sworn_staff} sworn` : '',
            displayData.civilian_staff ? `${displayData.civilian_staff} civilian` : ''
          ].filter(Boolean).join(', ')})`
        : ''}`
    : null;

  return (
    <div className="office-detail-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/address-book/jails')}>
            <FaArrowLeft /> Back
          </button>
        </div>

        <div className="header-center">
          <h1>{jail.name || 'Unnamed Facility'}</h1>
        </div>

        <div className="header-right">
          {displayData.roster_url && (
            <a
              href={displayData.roster_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-roster"
              title="View Inmate Roster"
            >
              <FaGlobe /> Roster
            </a>
          )}
          {user.role === 'admin' && !editMode && (
            <button className="btn-edit-office" onClick={() => setEditMode(true)}>
              <FaEdit /> Edit
            </button>
          )}
          {editMode && (
            <>
              <button className="btn-save" onClick={handleSaveJailEdit}>
                <FaSave /> Save
              </button>
              <button className="btn-cancel" onClick={handleCancelEdit}>
                <FaTimes /> Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main Content - Single Grid Layout */}
      <div className="detail-content-unified">
        <div className="unified-grid">

          {/* BASIC INFORMATION */}
          <div className="grid-section-header">Basic Information</div>

          {!editMode && displayData.ori_number && (
            <div className="grid-item full-width ori-highlight">
              <label>ORI Number</label>
              <span>{displayData.ori_number}</span>
            </div>
          )}

          {editMode && (
            <div className="grid-item full-width">
              <label>ORI Number</label>
              <input
                type="text"
                value={displayData.ori_number || ''}
                onChange={(e) => handleInputChange('ori_number', e.target.value)}
                placeholder="ORI Number"
              />
            </div>
          )}

          <div className="grid-item">
            <label>Facility Type</label>
            {editMode ? (
              <select
                value={displayData.facility_type || ''}
                onChange={(e) => handleInputChange('facility_type', e.target.value)}
              >
                <option value="">Select Type</option>
                <option value="County Jail">County Jail</option>
                <option value="Detention Center">Detention Center</option>
                <option value="Regional Facility">Regional Facility</option>
                <option value="Youth Detention">Youth Detention</option>
                <option value="Pre-Release Center">Pre-Release Center</option>
                <option value="Other">Other</option>
              </select>
            ) : (
              <span>{displayData.facility_type || 'N/A'}</span>
            )}
          </div>

          <div className="grid-item">
            <label>Operational Status</label>
            {editMode ? (
              <select
                value={displayData.operational_status || ''}
                onChange={(e) => handleInputChange('operational_status', e.target.value)}
              >
                <option value="">Select Status</option>
                <option value="Operational">Operational</option>
                <option value="Limited Operations">Limited Operations</option>
                <option value="Closed">Closed</option>
              </select>
            ) : (
              <span>{displayData.operational_status || 'N/A'}</span>
            )}
          </div>

          <div className="grid-item">
            <label>County</label>
            {editMode ? (
              <input
                type="text"
                value={displayData.county || ''}
                onChange={(e) => handleInputChange('county', e.target.value)}
                placeholder="County"
              />
            ) : (
              <span>{displayData.county || 'N/A'}</span>
            )}
          </div>

          {!editMode && fullAddress && (
            <div className="grid-item full-width">
              <label><FaMapMarkerAlt /> Address</label>
              <span>{fullAddress}</span>
            </div>
          )}

          {editMode && (
            <>
              <div className="grid-item full-width">
                <label><FaMapMarkerAlt /> Street Address</label>
                <input
                  type="text"
                  value={displayData.address_street || ''}
                  onChange={(e) => handleInputChange('address_street', e.target.value)}
                  placeholder="Street address"
                />
              </div>

              <div className="grid-item">
                <label>City</label>
                <input
                  type="text"
                  value={displayData.city || ''}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="City"
                />
              </div>

              <div className="grid-item">
                <label>State</label>
                <input
                  type="text"
                  value={displayData.address_state || ''}
                  onChange={(e) => handleInputChange('address_state', e.target.value)}
                  placeholder="State"
                />
              </div>

              <div className="grid-item">
                <label>ZIP Code</label>
                <input
                  type="text"
                  value={displayData.address_zip || ''}
                  onChange={(e) => handleInputChange('address_zip', e.target.value)}
                  placeholder="ZIP code"
                />
              </div>
            </>
          )}

          {/* CONTACT NUMBERS */}
          <div className="grid-section-header">Contact Numbers</div>

          {(displayData.phone || editMode) && (
            <div className="grid-item">
              <label><FaPhoneAlt /> Main Phone</label>
              {editMode ? (
                <input
                  type="tel"
                  value={displayData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Main phone"
                />
              ) : (
                <span>{displayData.phone || 'N/A'}</span>
              )}
            </div>
          )}

          {(displayData.emergency_number || editMode) && (
            <div className="grid-item">
              <label><FaExclamationTriangle /> Emergency</label>
              {editMode ? (
                <input
                  type="tel"
                  value={displayData.emergency_number || ''}
                  onChange={(e) => handleInputChange('emergency_number', e.target.value)}
                  placeholder="Emergency number"
                />
              ) : (
                <span>{displayData.emergency_number || 'N/A'}</span>
              )}
            </div>
          )}

          {(displayData.non_emergency_number || editMode) && (
            <div className="grid-item">
              <label><FaPhoneAlt /> Non-Emergency</label>
              {editMode ? (
                <input
                  type="tel"
                  value={displayData.non_emergency_number || ''}
                  onChange={(e) => handleInputChange('non_emergency_number', e.target.value)}
                  placeholder="Non-emergency number"
                />
              ) : (
                <span>{displayData.non_emergency_number || 'N/A'}</span>
              )}
            </div>
          )}

          {(displayData.booking_phone || editMode) && (
            <div className="grid-item">
              <label><FaPhoneAlt /> Booking Phone</label>
              {editMode ? (
                <input
                  type="tel"
                  value={displayData.booking_phone || ''}
                  onChange={(e) => handleInputChange('booking_phone', e.target.value)}
                  placeholder="Booking phone"
                />
              ) : (
                <span>{displayData.booking_phone || 'N/A'}</span>
              )}
            </div>
          )}

          {(displayData.medical_services_phone || editMode) && (
            <div className="grid-item">
              <label><FaPhoneAlt /> Medical Services</label>
              {editMode ? (
                <input
                  type="tel"
                  value={displayData.medical_services_phone || ''}
                  onChange={(e) => handleInputChange('medical_services_phone', e.target.value)}
                  placeholder="Medical services phone"
                />
              ) : (
                <span>{displayData.medical_services_phone || 'N/A'}</span>
              )}
            </div>
          )}

          {(displayData.phone_service_number || editMode) && (
            <div className="grid-item">
              <label><FaPhoneAlt /> Inmate Phone Service</label>
              {editMode ? (
                <input
                  type="tel"
                  value={displayData.phone_service_number || ''}
                  onChange={(e) => handleInputChange('phone_service_number', e.target.value)}
                  placeholder="Phone service number"
                />
              ) : (
                <span>{displayData.phone_service_number || 'N/A'}</span>
              )}
            </div>
          )}

          {(displayData.fax || editMode) && (
            <div className="grid-item">
              <label><FaFax /> Fax</label>
              {editMode ? (
                <input
                  type="tel"
                  value={displayData.fax || ''}
                  onChange={(e) => handleInputChange('fax', e.target.value)}
                  placeholder="Fax number"
                />
              ) : (
                <span>{displayData.fax || 'N/A'}</span>
              )}
            </div>
          )}

          {/* DIGITAL RESOURCES */}
          <div className="grid-section-header">Digital Resources</div>

          {(displayData.website || editMode) && (
            <div className="grid-item full-width">
              <label><FaGlobe /> Website</label>
              {editMode ? (
                <input
                  type="url"
                  value={displayData.website || ''}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="Website URL"
                />
              ) : (
                <span>
                  {displayData.website ? (
                    <a href={displayData.website} target="_blank" rel="noopener noreferrer">
                      {displayData.website}
                    </a>
                  ) : 'N/A'}
                </span>
              )}
            </div>
          )}

          {(displayData.email || editMode) && (
            <div className="grid-item full-width">
              <label><FaEnvelope /> Email</label>
              {editMode ? (
                <input
                  type="email"
                  value={displayData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Email address"
                />
              ) : (
                <span>
                  {displayData.email ? (
                    <a href={`mailto:${displayData.email}`}>{displayData.email}</a>
                  ) : 'N/A'}
                </span>
              )}
            </div>
          )}

          {(displayData.roster_url || editMode) && (
            <div className="grid-item full-width">
              <label><FaGlobe /> Inmate Roster</label>
              {editMode ? (
                <input
                  type="url"
                  value={displayData.roster_url || ''}
                  onChange={(e) => handleInputChange('roster_url', e.target.value)}
                  placeholder="Jail roster/inmate list URL"
                />
              ) : (
                <span>
                  {displayData.roster_url ? (
                    <a href={displayData.roster_url} target="_blank" rel="noopener noreferrer">
                      {displayData.roster_url}
                    </a>
                  ) : 'N/A'}
                </span>
              )}
            </div>
          )}

          {(displayData.commissary_website || editMode) && (
            <div className="grid-item full-width">
              <label><FaGlobe /> Commissary Site</label>
              {editMode ? (
                <input
                  type="url"
                  value={displayData.commissary_website || ''}
                  onChange={(e) => handleInputChange('commissary_website', e.target.value)}
                  placeholder="Commissary website URL"
                />
              ) : (
                <span>
                  {displayData.commissary_website ? (
                    <a href={displayData.commissary_website} target="_blank" rel="noopener noreferrer">
                      {displayData.commissary_website}
                    </a>
                  ) : 'N/A'}
                </span>
              )}
            </div>
          )}

          {(displayData.video_visit_website || editMode) && (
            <div className="grid-item full-width">
              <label><FaGlobe /> Video Visits</label>
              {editMode ? (
                <input
                  type="url"
                  value={displayData.video_visit_website || ''}
                  onChange={(e) => handleInputChange('video_visit_website', e.target.value)}
                  placeholder="Video visit website URL"
                />
              ) : (
                <span>
                  {displayData.video_visit_website ? (
                    <a href={displayData.video_visit_website} target="_blank" rel="noopener noreferrer">
                      {displayData.video_visit_website}
                    </a>
                  ) : 'N/A'}
                </span>
              )}
            </div>
          )}

          {(displayData.recruitment_website || editMode) && (
            <div className="grid-item full-width">
              <label><FaGlobe /> Recruitment</label>
              {editMode ? (
                <input
                  type="url"
                  value={displayData.recruitment_website || ''}
                  onChange={(e) => handleInputChange('recruitment_website', e.target.value)}
                  placeholder="Recruitment website URL"
                />
              ) : (
                <span>
                  {displayData.recruitment_website ? (
                    <a href={displayData.recruitment_website} target="_blank" rel="noopener noreferrer">
                      {displayData.recruitment_website}
                    </a>
                  ) : 'N/A'}
                </span>
              )}
            </div>
          )}

          {/* OPERATIONS */}
          <div className="grid-section-header">Operations</div>

          {(displayData.capacity || editMode) && (
            <div className="grid-item">
              <label>Capacity</label>
              {editMode ? (
                <input
                  type="number"
                  value={displayData.capacity || ''}
                  onChange={(e) => handleInputChange('capacity', e.target.value)}
                  placeholder="Capacity"
                />
              ) : (
                <span>{displayData.capacity || 'N/A'}</span>
              )}
            </div>
          )}

          {(displayData.book_release_hours || editMode) && (
            <div className="grid-item full-width">
              <label><FaClock /> Booking/Release Hours</label>
              {editMode ? (
                <input
                  type="text"
                  value={displayData.book_release_hours || ''}
                  onChange={(e) => handleInputChange('book_release_hours', e.target.value)}
                  placeholder="e.g., 24/7, Mon-Fri 8am-5pm"
                />
              ) : (
                <span>{displayData.book_release_hours || 'N/A'}</span>
              )}
            </div>
          )}

          {/* STAFFING & BUDGET */}
          <div className="grid-section-header">Staffing & Budget</div>

          {!editMode && staffDisplay && (
            <div className="grid-item full-width">
              <label><FaUsers /> Total Staff</label>
              <span>{staffDisplay}</span>
            </div>
          )}

          {editMode && (
            <>
              <div className="grid-item">
                <label><FaUsers /> Total Staff</label>
                <input
                  type="number"
                  value={displayData.total_staff || ''}
                  onChange={(e) => handleInputChange('total_staff', e.target.value)}
                  placeholder="Total staff"
                />
              </div>

              <div className="grid-item">
                <label><FaUsers /> Sworn Staff</label>
                <input
                  type="number"
                  value={displayData.sworn_staff || ''}
                  onChange={(e) => handleInputChange('sworn_staff', e.target.value)}
                  placeholder="Sworn staff"
                />
              </div>

              <div className="grid-item">
                <label><FaUsers /> Civilian Staff</label>
                <input
                  type="number"
                  value={displayData.civilian_staff || ''}
                  onChange={(e) => handleInputChange('civilian_staff', e.target.value)}
                  placeholder="Civilian staff"
                />
              </div>
            </>
          )}

          {(displayData.annual_budget || editMode) && (
            <div className="grid-item">
              <label><FaDollarSign /> Annual Budget</label>
              {editMode ? (
                <input
                  type="text"
                  value={displayData.annual_budget || ''}
                  onChange={(e) => handleInputChange('annual_budget', e.target.value)}
                  placeholder="Annual budget"
                />
              ) : (
                <span>{displayData.annual_budget || 'N/A'}</span>
              )}
            </div>
          )}

          {(displayData.annual_transports || editMode) && (
            <div className="grid-item">
              <label><FaTruck /> Annual Transports</label>
              {editMode ? (
                <input
                  type="number"
                  value={displayData.annual_transports || ''}
                  onChange={(e) => handleInputChange('annual_transports', e.target.value)}
                  placeholder="Annual transports"
                />
              ) : (
                <span>{displayData.annual_transports || 'N/A'}</span>
              )}
            </div>
          )}

          {/* MAILING ADDRESSES */}
          {(displayData.inmate_mail_address || displayData.legal_mail_address || editMode) && (
            <>
              <div className="grid-section-header">Mailing Addresses</div>

              {(displayData.inmate_mail_address || editMode) && (
                <div className="grid-item full-width">
                  <label><FaEnvelope /> Regular Mail</label>
                  {editMode ? (
                    <textarea
                      value={displayData.inmate_mail_address || ''}
                      onChange={(e) => handleInputChange('inmate_mail_address', e.target.value)}
                      placeholder="Address for inmate mail"
                      rows="2"
                    />
                  ) : (
                    <span style={{ whiteSpace: 'pre-line' }}>{displayData.inmate_mail_address || 'N/A'}</span>
                  )}
                </div>
              )}

              {(displayData.legal_mail_address || editMode) && (
                <div className="grid-item full-width">
                  <label><FaEnvelope /> Legal Mail</label>
                  {editMode ? (
                    <textarea
                      value={displayData.legal_mail_address || ''}
                      onChange={(e) => handleInputChange('legal_mail_address', e.target.value)}
                      placeholder="Address for legal mail"
                      rows="2"
                    />
                  ) : (
                    <span style={{ whiteSpace: 'pre-line' }}>{displayData.legal_mail_address || 'N/A'}</span>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* STAFF CONTACTS SECTION */}
        <div className="staff-contacts-section">
          <div className="staff-contacts-header">
            <h2>Staff Contacts</h2>
            {user.role === 'admin' && (
              <button className="btn-add-staff" onClick={() => setShowAddStaffModal(true)}>
                <FaPlus /> Add Staff
              </button>
            )}
          </div>

          {staff.length === 0 ? (
            <div className="no-staff-placeholder">
              <FaUser size={32} />
              <p>No staff contacts</p>
            </div>
          ) : (
            <div className="staff-list-compact">
              {staff.map((member) => (
                <div key={member.id} className="staff-card-compact">
                  <div className="staff-card-header">
                    <div className="staff-name">{member.name}</div>
                    {member.title && (
                      <span className="staff-title-badge">
                        {member.title}
                      </span>
                    )}
                  </div>
                  <div className="staff-card-details">
                    {member.phone && (
                      <div className="staff-detail-item">
                        <FaPhoneAlt size={12} />
                        <span>
                          {member.phone}
                          {member.phone_ext && ` ext. ${member.phone_ext}`}
                        </span>
                      </div>
                    )}
                    {member.email && (
                      <div className="staff-detail-item">
                        <FaEnvelope size={12} />
                        <a href={`mailto:${member.email}`}>{member.email}</a>
                      </div>
                    )}
                    {member.notes && (
                      <div className="staff-notes">{member.notes}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Staff Modal */}
      {showAddStaffModal && (
        <div className="modal-overlay" onClick={() => setShowAddStaffModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Staff Member</h2>
              <button className="modal-close" onClick={() => setShowAddStaffModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newStaff.name}
                  onChange={(e) => handleStaffInputChange('name', e.target.value)}
                  placeholder="Enter staff member name"
                />
              </div>

              <div className="form-group">
                <label>Title *</label>
                <select
                  value={newStaff.title}
                  onChange={(e) => handleStaffInputChange('title', e.target.value)}
                >
                  <option value="">Select Title</option>
                  <option value="Warden">Warden</option>
                  <option value="Detention Captain">Detention Captain</option>
                  <option value="Detention Sergeant">Detention Sergeant</option>
                  <option value="Booking Officer">Booking Officer</option>
                  <option value="Records Officer">Records Officer</option>
                  <option value="Medical Staff">Medical Staff</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newStaff.phone}
                    onChange={(e) => handleStaffInputChange('phone', e.target.value)}
                    placeholder="Phone number"
                  />
                </div>

                <div className="form-group">
                  <label>Extension</label>
                  <input
                    type="text"
                    value={newStaff.phone_ext}
                    onChange={(e) => handleStaffInputChange('phone_ext', e.target.value)}
                    placeholder="Ext."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newStaff.email}
                  onChange={(e) => handleStaffInputChange('email', e.target.value)}
                  placeholder="Email address"
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newStaff.notes}
                  onChange={(e) => handleStaffInputChange('notes', e.target.value)}
                  placeholder="Additional notes..."
                  rows="3"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAddStaffModal(false)}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleAddStaff}>
                <FaPlus /> Add Staff Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JailDetail;
